import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { ApiError } from '../../api/client';
import { humanizeError } from '../../lib/errors';
import {
  trainerUnitApi,
  type TrainerSkill,
  type TrainerUnit,
  type TypeState,
  type UnitGroup,
  type UnitRepeat,
  type UnitResult,
} from '../../api/ielts';
import { loadTrainerUnit } from '../../lib/trainerLoad';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { AudioPlayer } from '../../components/ui/AudioPlayer';
import { SourceSplit } from '../../components/ielts/SourceSplit';
import { useIeltsTypeLabel } from '../../hooks/useIeltsTypeLabel';
import { trainerTask } from '../../lib/trainerTask';
import { TaskCard } from '../../components/ielts/MockTaskCard';
import type { PoolSkill } from '../../api/ieltsMock';
import { readableAnswer } from '../../lib/mockReview';

// Тренировка идёт БЛОКАМИ, как настоящий экзамен: одна запись или один текст,
// внутри несколько заданий разных типов, в каждом несколько вопросов. Первая
// версия выдавала по одному вопросу без источника — аудирование в ней было
// неотвечаемо в принципе, а тренировать по одному вопросу нечего.



const STATE_STYLE: Record<TypeState, string> = {
  unknown: 'bg-surface-variant text-on-surface-variant',
  weak: 'bg-error-container text-on-error-container',
  strong: 'bg-primary-container text-on-primary-container',
};

// Подписи типов заданий берутся из общего переведённого источника
// (`useIeltsTypeLabel`): раньше свой русский словарь жил здесь, второй — в
// api/ieltsMock, и они успели разойтись в формулировках.

const isSkill = (v: string | undefined): v is TrainerSkill =>
  v === 'reading' || v === 'listening' || v === 'writing' || v === 'speaking';

const fmtDuration = (sec?: number) => {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export function IeltsTrainerPage() {
  const { t } = useTranslation();

  const { skill } = useParams<{ skill: string }>();
  const navigate = useNavigate();

  const [unit, setUnit] = useState<TrainerUnit | null>(null);
  // Причину подбора сервер присылает готовой ФРАЗОЙ и всегда по-русски, а
  // состояние («неизвестный/слабый/сильный») — отдельным полем. Берём
  // состояние и переводим сами: иначе ученик с английским интерфейсом читает
  // русскую строку посреди своей страницы.
  const reasonState =
    unit?.prompt?.state ??
    (unit?.groups ?? []).find((g) => g.state === 'unknown' || g.state === 'weak')?.state ??
    (unit?.groups ?? [])[0]?.state;
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<UnitResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // «Задания закончились» ученик не видит (решение владельца 30.10.2026): цепочка повторов и тихие
  // повторы запроса — lib/trainerLoad.ts; `creating` — только когда давать нечего и в повторе.
  const [creating, setCreating] = useState(false);
  const [failed, setFailed] = useState(false);
  const [repeat, setRepeat] = useState<UnitRepeat | undefined>(undefined);
  const [needsMock, setNeedsMock] = useState(false);
  const [error, setError] = useState('');
  // Сколько блоков пройдено за эту тренировку — накапливается локально.
  const [blocks, setBlocks] = useState(0);
  const [totals, setTotals] = useState({ done: 0, correct: 0 });

  const loadUnit = useCallback(async () => {
    if (!isSkill(skill)) return;
    setLoading(true);
    setError('');
    setResult(null);
    setAnswers({});
    setCreating(false);
    setFailed(false);
    setNeedsMock(false);
    // Клиент нормализует ошибки в плоский ApiError — статус лежит в e.status, а НЕ в
    // e.response.status (разбор — в lib/trainerLoad.ts).
    const got = await loadTrainerUnit((r) => trainerUnitApi.next(skill, r));
    setUnit(got.kind === 'unit' ? got.unit : null);
    setRepeat(got.kind === 'unit' ? got.repeat : undefined);
    if (got.kind === 'needsMock') setNeedsMock(true);
    else if (got.kind === 'creating') setCreating(true);
    else if (got.kind === 'failed') setFailed(true);
    // Закрыто, вне расписания, лимит — причина, а не «не удалось загрузить» (промпт 12, 18.10.2026).
    else if (got.kind === 'gate') setError(humanizeError(got.error as ApiError));
    setLoading(false);
  }, [skill]);

  useEffect(() => {
    void loadUnit();
  }, [loadUnit]);

  const questionCount = useMemo(
    () => (unit?.groups ?? []).reduce((n, g) => n + g.questions.length, 0),
    [unit],
  );

  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v.trim() !== '').length,
    [answers],
  );

  async function submit() {
    if (!unit || busy) return;
    setBusy(true);
    setError('');
    try {
      // Отправляем ВСЕ вопросы блока, включая пропущенные: на экзамене
      // пустой ответ тоже не приносит балла, и картина должна это видеть.
      const payload = (unit.groups ?? []).flatMap((g) =>
        g.questions.map((q) => ({ question_id: q.id, answer: answers[q.id] ?? '' })),
      );
      const res = await trainerUnitApi.submit(unit.session_id, payload);
      setResult(res);
      setBlocks((n) => n + 1);
      setTotals((t) => ({ done: t.done + res.total, correct: t.correct + res.correct }));
    } catch {
      setError(t('trainer.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (!isSkill(skill)) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Card>{t('trainer.unknownSkill')}</Card>
      </div>
    );
  }

  const accuracy = totals.done ? Math.round((totals.correct / totals.done) * 100) : 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {t('trainer.titleWithSkill', { skill: t(`trainer.skill.${skill}`) })}
          </h1>
          <p className="text-sm text-on-surface-variant">
            {reasonState
              ? t('trainer.reason', { reason: t(`trainer.reasonBy.${reasonState}`) })
              : t('trainer.freePlay')}
          </p>
        </div>
        {totals.done > 0 && (
          <div className="text-right">
            <div className="font-mono text-2xl font-bold">
              {totals.correct}/{totals.done}
            </div>
            <div className="text-xs uppercase tracking-wide text-on-surface-variant">
              {t('trainer.sessionStat', { accuracy, count: blocks })}
            </div>
          </div>
        )}
      </header>

      {repeat && unit && (
        <div className="mb-4" data-trainer-repeat={repeat}>
          <Pill variant="warning">{t(repeat === 'mistakes' ? 'trainer.repeatMistakes' : 'trainer.repeatPassed')}</Pill>
        </div>
      )}

      {error && (
        <Card className="mb-4 border-error/40 bg-error-container">
          <p className="text-sm text-on-error-container">{error}</p>
        </Card>
      )}

      {needsMock ? (
        <Card className="text-center">
          <h2 className="mb-2 text-lg font-semibold">{t('trainer.mockFirstTitle')}</h2>
          <p className="mb-4 text-on-surface-variant">
            {t('trainer.mockFirstDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => navigate('/app/ielts/mock')}>{t('trainer.goToMock')}</Button>
            <Button variant="ghost" onClick={() => navigate('/app/trainers')}>{t('trainer.backToTrainers')}</Button>
          </div>
        </Card>
      ) : creating ? (
        <Card className="text-center" data-trainer-creating>
          <p className="mb-4 text-on-surface">{t('trainer.creatingText')}</p>
          <Button variant="ghost" onClick={() => navigate('/app/trainers')}>{t('trainer.backToTrainers')}</Button>
        </Card>
      ) : failed && !loading ? (
        <Card className="text-center" data-trainer-failed>
          <p className="mb-4 text-on-surface-variant">{t('trainer.loadFailed')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => void loadUnit()}>{t('trainer.retry')}</Button>
            <Button variant="ghost" onClick={() => navigate('/app/trainers')}>{t('trainer.backToTrainers')}</Button>
          </div>
        </Card>
      ) : loading ? (
        <div className="grid min-h-[40vh] place-items-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary">
            progress_activity
          </span>
        </div>
      ) : !unit ? null : result ? (
        <UnitReview
          result={result}
          unit={unit}
          onNext={() => void loadUnit()}
          onExit={() => navigate('/app/trainers')}
        />
      ) : unit.prompt ? (
        <PromptBlock unit={unit} onExit={() => navigate('/app/trainers')} />
      ) : (
        <SourceSplit
          // Аудирование ведёт себя иначе: плеер нельзя прятать за вкладку,
          // иначе ученик не сможет поставить паузу, не переключившись.
          kind={unit.source?.audio_url && !unit.source?.body ? 'audio' : 'text'}
          source={<SourcePane unit={unit} />}
          paneClassName="lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:rounded-[22px] lg:bg-surface-container lg:p-5"
          questions={
          <div className="flex flex-col gap-4">
            {(unit.groups ?? []).map((g, gi) => (
              <GroupCard
                key={g.task_id}
                group={g}
                index={gi}
                skill={unit.skill}
                answers={answers}
                onAnswer={(qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }))}
              />
            ))}

            <Card className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-on-surface-variant">
                {t('trainer.answered', { done: answeredCount, total: questionCount })}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => navigate('/app/trainers')}>{t('trainer.exit')}</Button>
                <Button onClick={submit} disabled={busy}>
                  {busy ? t('trainer.checking') : t('trainer.checkBlock')}
                </Button>
              </div>
            </Card>
          </div>
          }
        />
      )}
    </div>
  );
}

/** Источник: запись для аудирования, отрывок для чтения. */
function SourcePane({ unit }: { unit: TrainerUnit }) {
  const { t } = useTranslation();
  const src = unit.source;
  if (!src) return null;

  return (
    <Card className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
      <h2 className="mb-1 text-lg font-semibold">{src.title || t('trainer.material')}</h2>

      {src.audio_url && (
        <div className="mb-3">
          <p className="mb-2 text-xs uppercase tracking-wide text-on-surface-variant">
            {t('trainer.recording')}
            {src.duration_sec ? ` · ${fmtDuration(src.duration_sec)}` : ''}
          </p>
          {/* Перемотка намеренно доступна: это тренировка, а не экзамен. */}
          <AudioPlayer src={src.audio_url} />
        </div>
      )}

      {src.body && (
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-on-surface">
          {src.body}
        </div>
      )}

      {src.audio_url && !src.body && (
        <p className="text-sm text-on-surface-variant">
          {t('trainer.listenHint')}
        </p>
      )}
    </Card>
  );
}

/** Одно задание блока: свой тип, своя инструкция, свои вопросы. */
function GroupCard({
  group,
  index,
  skill,
  answers,
  onAnswer,
}: {
  group: UnitGroup;
  index: number;
  skill: PoolSkill;
  answers: Record<string, string>;
  onAnswer: (questionId: string, value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_STYLE[group.state]}`}>
          {t(`trainer.state.${group.state}`)}
        </span>
        <span className="font-semibold">{t('trainer.taskNoShort', { index: index + 1 })}</span>
        <span className="text-sm text-on-surface-variant">· {group.reason}</span>
      </div>
      {/* Задание — карточкой мока и спринта (T3, виды как на engnovate, lib/trainerTask.ts): поле в
          строке, буквы у выбора, рамка у заметок — и на телефоне так же. Вид, инструкцию и общий
          список показывает сама карточка; значения ответов прежние (CLOSED_CHOICES, текст варианта). */}
      <TaskCard task={trainerTask(group, skill)} answers={answers} onAnswer={onAnswer} />
    </div>
  );
}

/** Письмо и речь: задание одно, проверяет его AI-экзаменатор. */
function PromptBlock({ unit, onExit }: { unit: TrainerUnit; onExit: () => void }) {
  const { t } = useTranslation();
  const p = unit.prompt!;
  const navigate = useNavigate();
  const target = unit.skill === 'writing' ? '/app/ielts/writing' : '/app/ielts/speaking';

  // Задание едет на экран ответа вместе с переходом. Без этого там
  // открывался каталог, и подобранное тренажёром задание терялось.
  const handoff = {
    id: p.id,
    kind: p.kind,
    part: p.part,
    title: p.topic || (unit.skill === 'writing' ? 'Writing Task' : 'Speaking'),
    text: p.text,
    image_url: p.image_url,
    // Карточка Part 2 — единственная часть, которую живой экзаменатор
    // принимает как параметр; для частей 1 и 3 тему задаёт сам разговор.
    cue_card_id: p.part === 2 ? p.id : undefined,
  };

  return (
    <Card className="mx-auto max-w-3xl">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_STYLE[p.state]}`}>
          {t(`trainer.state.${p.state}`)}
        </span>
        <span className="font-semibold">
          {unit.skill === 'writing'
            ? p.kind === 'task2'
              ? 'Writing Task 2'
              : 'Writing Task 1'
            : `Speaking Part ${p.part || 1}`}
        </span>
        <span className="text-sm text-on-surface-variant">· {p.reason}</span>
      </div>

      {p.topic && <h2 className="mb-2 text-lg font-semibold">{p.topic}</h2>}
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{p.text}</p>

      {p.image_url && (
        <img src={p.image_url} alt="" className="mt-3 max-w-full rounded-lg" loading="lazy" />
      )}

      {p.bullets && p.bullets.length > 0 && (
        <ul className="mt-3 grid gap-1 text-[15px]">
          {p.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-on-surface-variant">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => navigate(target, { state: { trainerPrompt: handoff } })}>
          {unit.skill === 'writing' ? t('trainer.startWriting') : t('trainer.startSpeaking')}
        </Button>
        <Button variant="ghost" onClick={onExit}>{t('trainer.backToTrainers')}</Button>
      </div>
    </Card>
  );
}

/** Разбор блока: по каждому вопросу — что было, что верно и почему. */
function UnitReview({
  result,
  unit,
  onNext,
  onExit,
}: {
  result: UnitResult;
  unit: TrainerUnit;
  onNext: () => void;
  onExit: () => void;
}) {
  const { t } = useTranslation();
  const typeLabel = useIeltsTypeLabel();
  const pct = Math.round(result.accuracy * 100);

  return (
    <div className="grid gap-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{t('trainer.checkedTitle')}</h2>
            <p className="text-sm text-on-surface-variant">
              {t('trainer.score', { correct: result.correct, total: result.total, pct })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onExit}>
              {t('trainer.finish')}
            </Button>
            <Button onClick={onNext}>{t('trainer.nextBlock')}</Button>
          </div>
        </div>

        {Object.keys(result.by_type).length > 0 && (
          <div className="mt-4 grid gap-1 text-sm">
            {Object.entries(result.by_type).map(([t, v]) => (
              <div key={t} className="flex justify-between gap-3">
                <span>{typeLabel(t)}</span>
                <span className="font-mono tabular-nums text-on-surface-variant">
                  {v.correct}/{v.total}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {result.transcript && (
        <Card>
          <h3 className="mb-2 font-semibold">{t('trainer.transcript')}</h3>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-on-surface-variant">
            {result.transcript}
          </p>
        </Card>
      )}

      {unit.source?.body && (
        <Card>
          <h3 className="mb-2 font-semibold">{unit.source.title || t('trainer.sourceText')}</h3>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-on-surface-variant">
            {unit.source.body}
          </p>
        </Card>
      )}

      <Card>
        <h3 className="mb-3 font-semibold">{t('trainer.review')}</h3>
        <ol className="grid gap-4">
          {result.review.map((r, i) => (
            <li
              key={r.question_id}
              className={`rounded-lg border p-3 ${
                r.correct ? 'border-primary/40 bg-primary-container/30' : 'border-error/40 bg-error-container/30'
              }`}
            >
              <div className="mb-1 flex items-start gap-2">
                <span className="material-symbols-outlined text-base">
                  {r.correct ? 'check_circle' : 'cancel'}
                </span>
                <span className="text-[15px]">
                  <b className="mr-1 text-on-surface-variant">{i + 1}.</b>
                  {r.prompt}
                </span>
              </div>
              <p className="text-sm">
                {/* «Выберите ДВА» — «A; B», а не служебное «A || B». */}
                {t('trainer.gaveAnswer')} <b>{(r.type === 'mc_multi' ? readableAnswer(r.type, r.given) : r.given) || t('trainer.skipped')}</b>
                {!r.correct && (
                  <>
                    {' · '}{t('trainer.correctIs')} <b>{r.type === 'mc_multi' ? readableAnswer(r.type, r.correct_answer) : r.correct_answer}</b>
                  </>
                )}
              </p>
              {r.explanation && (
                <p className="mt-2 text-sm text-on-surface-variant">{r.explanation}</p>
              )}
              {r.evidence && (
                <blockquote className="mt-2 border-l-2 border-outline/50 pl-3 text-sm italic text-on-surface-variant">
                  «{r.evidence}»
                  {r.evidence_ref && <span className="not-italic"> — {r.evidence_ref}</span>}
                </blockquote>
              )}
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}
